import React, { useEffect, useState } from "react";
import { Plus, Search, Edit, Trash2, PackagePlus, Wheat, ShieldCheck, AlertCircle, CreditCard, Printer } from "lucide-react";
import Pin2Prompt from "../components/ui/Pin2Prompt";
import api, { getPin2 } from "../lib/api";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input, Label, Select, Textarea } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { toast } from "../components/ui/Toast";
import { useI18n } from "../contexts/I18nContext";
import { formatVND, formatDate, parseApiError} from "../lib/utils";
import { useSort } from "../hooks/useSort";
import { useFeature } from "../contexts/CreatorConfigContext";

const EMPTY = {
  name: "", code: "", unit: "kg", stock: 0, cost: 0,
  low_stock_threshold: 0, expiration_days: 30, notes: "", is_active: true,
};

const EMPTY_STOCK = {
  kind: "material", target_id: "", target_name: "", unit: "",
  quantity: 0, unit_price: 0,
  supplier_id: "", supplier_name: "",
  production_date: "", expiration_date: "", batch_code: "", notes: "",
  payment_method: "cash", paid_amount: 0, due_date: "",
};

export default function Materials() {
  const { t } = useI18n();
  const canMaterialsStockIn = useFeature("materials.stockin");
  const canMaterialsDelete  = useFeature("materials.delete");
  const canMaterialsExport  = useFeature("materials.export");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const [stockOpen, setStockOpen] = useState(false);
  const [stockForm, setStockForm] = useState(EMPTY_STOCK);
  const [suppliers, setSuppliers] = useState([]);
  const [movements, setMovements] = useState([]);

  // PIN2
  const [pin2Open, setPin2Open] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);

  const { sorted, handleSort, SortIcon, sortKey } = useSort(items, "name", "asc");

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    const { data } = await api.get("/materials", { params });
    setItems(data);
  };

  const loadMovements = async (materialId) => {
    try {
      const { data } = await api.get("/stock/movements", { params: { kind: "material", target_id: materialId } });
      setMovements(data);
    } catch { setMovements([]); }
  };

  useEffect(() => {
    load();
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };

  const openEditWithPin2 = (m) => {
    const pin = getPin2();
    if (pin) {
      setEditing(m); setForm({ ...EMPTY, ...m }); setOpen(true);
    } else {
      setPendingEdit(m); setPin2Open(true);
    }
  };

  const onPin2Verified = () => {
    setPin2Open(false);
    if (pendingEdit) {
      setEditing(pendingEdit); setForm({ ...EMPTY, ...pendingEdit });
      setPendingEdit(null); setOpen(true);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/materials/${editing.material_id}`, form);
      else await api.post("/materials", form);
      toast({ title: t("common.saved"), variant: "success" });
      setOpen(false); load();
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`${t("common.delete")} "${m.name}"?`)) return;
    try {
      await api.delete(`/materials/${m.material_id}`);
      toast({ title: t("common.deleted"), variant: "success" }); load();
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    }
  };

  const openStockIn = (m) => {
    const selectedSupplier = suppliers[0] || null;
    setStockForm({
      ...EMPTY_STOCK,
      kind: "material",
      target_id: m.material_id,
      target_name: m.name,
      unit: m.unit,
    });
    setStockOpen(true);
    loadMovements(m.material_id);
  };

  const submitStockIn = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...stockForm };
      if (!payload.production_date) delete payload.production_date;
      if (!payload.expiration_date) delete payload.expiration_date;
      if (!payload.due_date) delete payload.due_date;
      if (!payload.supplier_id) { delete payload.supplier_id; delete payload.supplier_name; }
      // Nếu TM/CK trả full -> paid_amount = subtotal
      if (payload.payment_method !== "debt") {
        payload.paid_amount = payload.unit_price * payload.quantity;
      }
      await api.post("/stock/in", payload);
      toast({ title: "Nhập kho thành công!", variant: "success" });
      setStockOpen(false); load();
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    }
  };

  // Selected supplier info for CK hint
  const selectedSupplier = suppliers.find(s => s.supplier_id === stockForm.supplier_id);
  const subtotal = (stockForm.unit_price || 0) * (stockForm.quantity || 0);
  const debtAmount = stockForm.payment_method === "debt" ? subtotal - (stockForm.paid_amount || 0) : 0;

  // Auto-fill due_date from supplier.debt_days
  const handleSupplierChange = (e) => {
    const s = suppliers.find(x => x.supplier_id === e.target.value);
    const updates = { supplier_id: e.target.value, supplier_name: s?.name || "" };
    if (s?.debt_days > 0) {
      const d = new Date();
      d.setDate(d.getDate() + s.debt_days);
      updates.due_date = d.toISOString().slice(0, 10);
    }
    setStockForm({ ...stockForm, ...updates });
  };

  const printAllMovements = async () => {
    try {
      const { data } = await api.get("/stock/movements", { params: { kind: "material" } });
      const style = `<style>
        body{font-family:Arial,sans-serif;font-size:12px;margin:20px}
        h2{margin:0 0 4px}p.sub{color:#555;font-size:11px;margin:0 0 12px}
        table{width:100%;border-collapse:collapse}
        th,td{border:1px solid #ccc;padding:5px 8px;text-align:left}
        th{background:#f0f0f0;font-size:11px;text-transform:uppercase}
        tfoot td{font-weight:bold}
        tr:nth-child(even) td{background:#fafafa}
        .in{color:#059669}.out{color:#dc2626}
        @media print{body{margin:10mm}}
      </style>`;
      const rows = data.map((m, i) => {
        const qty = Number(m.quantity) || 0;
        const isIn = qty > 0;
        return `<tr>
          <td>${i + 1}</td>
          <td>${m.created_at ? m.created_at.slice(0, 10) : "—"}</td>
          <td>${m.target_name || "—"}</td>
          <td class="${isIn ? "in" : "out"}">${isIn ? "Nhập" : "Xuất"}</td>
          <td style="text-align:right">${isIn ? "+" : ""}${qty} ${m.unit || ""}</td>
          <td style="text-align:right">${m.subtotal > 0 ? m.subtotal.toLocaleString("vi-VN") + "₫" : "—"}</td>
          <td>${m.batch_code || "—"}</td>
          <td>${m.supplier_name || "—"}</td>
          <td>${m.notes || ""}</td>
        </tr>`;
      }).join("");
      const html = `<!DOCTYPE html><html lang="vi"><head><meta charset="UTF-8"/>
        <title>Danh sách xuất nhập kho NVL</title>${style}</head><body>
        <h2>Danh sách xuất nhập kho — Nguyên vật liệu</h2>
        <p class="sub">${data.length} phiếu · In ngày ${new Date().toLocaleDateString("vi-VN")}</p>
        <table>
          <thead><tr>
            <th>STT</th><th>Ngày</th><th>Tên NVL</th><th>Loại</th>
            <th style="text-align:right">Số lượng</th><th style="text-align:right">Thành tiền</th>
            <th>Mã lô</th><th>Nhà cung cấp</th><th>Ghi chú</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <script>window.onload=()=>{window.print()}<\/script>
      </body></html>`;
      const win = window.open("", "_blank");
      win.document.write(html);
      win.document.close();
    } catch {
      toast({ title: "Không thể tải dữ liệu xuất nhập", variant: "error" });
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="materials-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <Wheat size={28} className="text-bamboo" /> Nguyên vật liệu
          </h1>
          <p className="text-sm text-ink-secondary mt-1">{items.length} NVL</p>
        </div>
        <div className="flex items-center gap-2">
          {canMaterialsExport && (
            <Button variant="outline" onClick={printAllMovements} data-testid="materials-print-button">
              <Printer size={16} /> In xuất nhập
            </Button>
          )}
          <Button onClick={openCreate} data-testid="materials-new-button">
            <Plus size={16} /> Thêm NVL
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent>
          <form onSubmit={(e) => { e.preventDefault(); load(); }}>
            <Label>Tìm kiếm</Label>
            <div className="relative max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tên / Mã NVL" className="pl-8" data-testid="materials-search" />
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Sort */}
      <div className="flex items-center gap-2 flex-wrap text-xs text-ink-muted">
        <span className="font-medium">Sắp xếp:</span>
        {[["name","Tên"],["stock","Tồn kho"],["cost","Giá vốn"],["code","Mã"]].map(([key,label]) => (
          <button key={key} onClick={() => handleSort(key)}
            className={`px-2 py-1 rounded border transition-colors ${sortKey===key ? "border-bamboo text-bamboo bg-bamboo/5" : "border-border hover:border-bamboo/50"}`}>
            {label}{SortIcon(key)}
          </button>
        ))}
      </div>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto overflow-y-auto max-h-[60vh]">
          <table className="w-full text-sm">
            <thead className="bg-cream/40 text-xs uppercase text-ink-muted sticky top-0 z-10">
              <tr>
                <th className="text-left py-3 px-4 bg-cream/40">Mã</th>
                <th className="text-left py-3 px-4 bg-cream/40 cursor-pointer hover:text-ink" onClick={() => handleSort("name")}>Tên NVL{SortIcon("name")}</th>
                <th className="text-right py-3 px-4 bg-cream/40 cursor-pointer hover:text-ink" onClick={() => handleSort("stock")}>Tồn kho{SortIcon("stock")}</th>
                <th className="text-right py-3 px-4 bg-cream/40 cursor-pointer hover:text-ink" onClick={() => handleSort("cost")}>Giá vốn/đvt{SortIcon("cost")}</th>
                <th className="text-right py-3 px-4 bg-cream/40">Ngưỡng</th>
                <th className="text-center py-3 px-4 bg-cream/40">Trạng thái</th>
                <th className="py-3 px-4 bg-cream/40"></th>
              </tr>
            </thead>
            <tbody data-testid="materials-table-body">
              {items.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-ink-muted">{t("common.empty")}</td></tr>
              ) : sorted.map((m) => {
                const low = m.stock <= m.low_stock_threshold;
                return (
                  <tr key={m.material_id} className="border-t border-border hover:bg-cream/30" data-testid={`material-row-${m.material_id}`}>
                    <td className="py-3 px-4 font-mono text-xs text-ink-muted">{m.code}</td>
                    <td className="py-3 px-4 font-medium">{m.name}</td>
                    <td className={`py-3 px-4 text-right font-mono ${low ? "text-red-700 font-bold" : ""}`}>{m.stock} {m.unit}</td>
                    <td className="py-3 px-4 text-right font-mono text-xs">{formatVND(m.cost)}</td>
                    <td className="py-3 px-4 text-right text-xs text-ink-muted">{m.low_stock_threshold} {m.unit}</td>
                    <td className="py-3 px-4 text-center">
                      {low
                        ? <Badge variant="lowstock"><AlertCircle size={10}/> Tồn thấp</Badge>
                        : <Badge variant="delivered">OK</Badge>
                      }
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-1">
                        {canMaterialsStockIn && (
                          <Button variant="ghost" size="icon" onClick={() => openStockIn(m)} title="Nhập kho" data-testid={`material-stockin-${m.material_id}`}>
                            <PackagePlus size={14} className="text-bamboo" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => openEditWithPin2(m)} title="Chỉnh sửa" data-testid={`material-edit-${m.material_id}`} className="relative">
                          <Edit size={14} />
                          <ShieldCheck size={8} className="absolute bottom-0.5 right-0.5 text-amber-500" />
                        </Button>
                        {canMaterialsDelete && (
                          <Button variant="ghost" size="icon" onClick={() => remove(m)} data-testid={`material-delete-${m.material_id}`}>
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create/Edit NVL Modal */}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Sửa nguyên vật liệu" : "Thêm nguyên vật liệu"} size="md" testId="material-modal">
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tên NVL *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="material-form-name" />
            </div>
            <div>
              <Label>Mã NVL</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Tự sinh" />
            </div>
            <div>
              <Label>Đơn vị tính</Label>
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="kg, lít, cái..." />
            </div>
            <div>
              <Label>Hạn sử dụng (ngày mặc định)</Label>
              <Input type="number" min={0} value={form.expiration_days} onChange={(e) => setForm({ ...form, expiration_days: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Tồn ban đầu</Label>
              <Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Giá vốn / đơn vị</Label>
              <Input type="number" min={0} value={form.cost} onChange={(e) => setForm({ ...form, cost: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Ngưỡng cảnh báo tồn thấp</Label>
              <Input type="number" min={0} value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: Number(e.target.value) })} />
            </div>
          </div>
          <div>
            <Label>Ghi chú</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" data-testid="material-form-submit">{t("common.save")}</Button>
          </div>
        </form>
      </Modal>

      {/* Nhập kho Modal */}
      <Modal open={stockOpen} onClose={() => setStockOpen(false)} title={`Nhập kho: ${stockForm.target_name}`} size="lg" testId="stock-in-modal">
        <form onSubmit={submitStockIn} className="space-y-4">

          {/* Thông tin hàng hóa */}
          <div className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Thông tin nhập kho</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nhà cung cấp</Label>
              <Select value={stockForm.supplier_id} onChange={handleSupplierChange}>
                <option value="">— Chọn NCC —</option>
                {suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
              </Select>
            </div>
            <div>
              <Label>Mã lô hàng</Label>
              <Input value={stockForm.batch_code} onChange={(e) => setStockForm({ ...stockForm, batch_code: e.target.value })} placeholder="Tự sinh" />
            </div>
            <div>
              <Label>Số lượng *</Label>
              <Input type="number" min={0} step="any" required value={stockForm.quantity}
                onChange={(e) => setStockForm({ ...stockForm, quantity: Number(e.target.value) })}
                data-testid="stock-in-qty" />
            </div>
            <div>
              <Label>Đơn giá</Label>
              <Input type="number" min={0} value={stockForm.unit_price}
                onChange={(e) => setStockForm({ ...stockForm, unit_price: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Ngày sản xuất</Label>
              <Input type="date" value={stockForm.production_date} onChange={(e) => setStockForm({ ...stockForm, production_date: e.target.value })} />
            </div>
            <div>
              <Label>Hạn sử dụng</Label>
              <Input type="date" value={stockForm.expiration_date} onChange={(e) => setStockForm({ ...stockForm, expiration_date: e.target.value })} />
            </div>
          </div>

          {/* Thành tiền */}
          {subtotal > 0 && (
            <div className="flex items-center justify-between bg-bamboo/5 border border-bamboo/20 rounded-md px-4 py-2">
              <span className="text-sm text-ink-muted">Thành tiền</span>
              <span className="text-lg font-bold text-bamboo">{formatVND(subtotal)}</span>
            </div>
          )}

          {/* Thanh toán */}
          <div className="text-xs font-semibold text-ink-muted uppercase tracking-wide pt-1">Hình thức thanh toán</div>
          <div>
            <Label>Thanh toán</Label>
            <Select value={stockForm.payment_method}
              onChange={(e) => setStockForm({ ...stockForm, payment_method: e.target.value, paid_amount: 0 })}>
              <option value="cash">Tiền mặt (TM) — trả ngay</option>
              <option value="transfer">Chuyển khoản (CK) — trả ngay</option>
              <option value="debt">Công nợ — hẹn thanh toán sau</option>
            </Select>
          </div>

          {stockForm.payment_method === "debt" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Trả trước (nếu có)</Label>
                <Input type="number" min={0} max={subtotal} value={stockForm.paid_amount}
                  onChange={(e) => setStockForm({ ...stockForm, paid_amount: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Ngày hẹn thanh toán</Label>
                <Input type="date" value={stockForm.due_date} onChange={(e) => setStockForm({ ...stockForm, due_date: e.target.value })} />
              </div>
              {debtAmount > 0 && (
                <div className="col-span-2 flex items-center gap-2 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                  <AlertCircle size={14} className="text-red-500 shrink-0"/>
                  <div className="text-sm">
                    <span className="text-ink-muted">Công nợ phát sinh: </span>
                    <span className="font-bold text-red-700">{formatVND(debtAmount)}</span>
                    {stockForm.due_date && <span className="text-xs text-ink-muted ml-2">· Hạn: {stockForm.due_date}</span>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Hint STK NCC nếu CK */}
          {stockForm.payment_method === "transfer" && selectedSupplier?.bank_account && (
            <div className="flex items-center gap-2 bg-bamboo/5 border border-bamboo/20 rounded-md px-3 py-2 text-sm">
              <CreditCard size={14} className="text-bamboo shrink-0"/>
              <div>
                <div className="font-semibold">{selectedSupplier.bank_account_holder || selectedSupplier.name}</div>
                <div className="text-xs text-ink-muted">{selectedSupplier.bank_name} · <span className="font-mono font-bold">{selectedSupplier.bank_account}</span></div>
              </div>
            </div>
          )}

          <div>
            <Label>Ghi chú</Label>
            <Textarea value={stockForm.notes} onChange={(e) => setStockForm({ ...stockForm, notes: e.target.value })} />
          </div>

          {/* Lịch sử nhập gần đây */}
          {movements.length > 0 && (
            <div className="border-t border-border pt-3">
              <div className="text-xs font-medium text-ink-muted mb-2">Lịch sử nhập gần đây:</div>
              <div className="space-y-1 max-h-36 overflow-y-auto">
                {movements.slice(0, 5).map((m) => (
                  <div key={m.movement_id} className="text-xs flex justify-between bg-cream/40 px-2 py-1.5 rounded">
                    <span>{formatDate(m.created_at)} · {m.quantity > 0 ? "+" : ""}{m.quantity} {m.unit || ""}</span>
                    <span className="flex items-center gap-2">
                      {m.subtotal > 0 && <span className="font-mono">{formatVND(m.subtotal)}</span>}
                      {m.payment_method === "debt" && <span className="text-red-500">Công nợ</span>}
                      {m.expiration_date && <span className="text-ink-muted">HSD: {formatDate(m.expiration_date)}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => setStockOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" data-testid="stock-in-submit">Nhập kho</Button>
          </div>
        </form>
      </Modal>

      {/* PIN2 */}
      <Pin2Prompt
        open={pin2Open}
        onClose={() => { setPin2Open(false); setPendingEdit(null); }}
        onVerified={onPin2Verified}
        action="chỉnh sửa thông tin nguyên vật liệu"
      />
    </div>
  );
}